import React from 'react';
import type { StaffInfo } from '../types';
import { Modal, StatusBadge, Avatar } from './ui';
import { formatDate } from '../lib/format';

interface Props {
  staff: StaffInfo | null;
  onClose: () => void;
}

const Row = ({ label, value }: { label: string; value?: React.ReactNode }) => (
  <div className="flex items-start justify-between gap-4 py-2 border-b border-gray-100 last:border-b-0">
    <span className="text-sm text-gray-500">{label}</span>
    <span className="text-sm font-medium text-right capitalize">{value || '—'}</span>
  </div>
);

export default function StaffDetailModal({ staff, onClose }: Props) {
  return (
    <Modal open={!!staff} onClose={onClose} title="Staff Information">
      {staff && (
        <div>
          <div className="flex items-center space-x-4 mb-6">
            <Avatar src={staff.image_url} name={staff.name} className="w-16 h-16" />
            <div>
              <p className="text-lg font-semibold">{staff.name}</p>
              <p className="text-sm text-gray-500 capitalize">{staff.role}</p>
              {staff.role_title && (
                <span className="inline-block mt-1 px-2.5 py-0.5 rounded-full bg-primary/10 text-primary-dark text-xs font-medium capitalize">
                  {staff.role_title}
                </span>
              )}
            </div>
            {staff.is_active !== undefined && (
              <div className="ml-auto">
                <StatusBadge status={staff.is_active ? 'approved' : 'rejected'} />
              </div>
            )}
          </div>

          <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wide mb-1">Contact</h3>
          <div className="mb-5">
            <Row label="Email" value={staff.email} />
            <Row label="Phone" value={staff.phone} />
            <Row label="Estate" value={staff.estate} />
            <Row label="Role Title" value={staff.role_title} />
            <Row label="Joined" value={staff.created_at ? formatDate(staff.created_at) : undefined} />
          </div>

          {(staff.bank_name || staff.bank_account || staff.account_name) && (
            <>
              <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wide mb-1">Salary & Bank Details</h3>
              <div className="mb-5">
                <Row label="Bank Name" value={staff.bank_name} />
                <Row label="Account Number" value={staff.bank_account} />
                <Row label="Account Name" value={staff.account_name} />
              </div>
            </>
          )}

          {(staff.next_of_kin_name || staff.next_of_kin_phone) && (
            <>
              <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wide mb-1">Next of Kin</h3>
              <div className="mb-5">
                <Row label="Full Name" value={staff.next_of_kin_name} />
                <Row label="Phone" value={staff.next_of_kin_phone} />
                <Row label="Relationship" value={staff.next_of_kin_relationship} />
              </div>
            </>
          )}
        </div>
      )}
    </Modal>
  );
}
